import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KPI } from '@/components/ui/kpi';
import { CompactKpiCard } from '@/components/kpi-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany } from '@/contexts/CompanyContext';
import { useWorkspaceSettings } from '@/contexts/WorkspaceSettingsContext';
import { TIMESHEET_PRODUCT_LABELS, type TimesheetProduct } from '@/types/integrations-redesign';
import { fetchFinOpsWebUrl } from '@/lib/finops-api';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  Filter,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  TrendingUp,
  Users,
  Maximize2,
  Minimize2,
  Link2Off,
  TimerOff,
  Settings2,
  Download,
  Briefcase,
  Building2,
  Tag,
  CalendarDays,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { MultiSelect } from '@/components/ui/multiselect';
import type { TimeEntry } from '@/types';
import type { FinOpsTimesheetEntry } from '@/types/finops.types';
import type { AccessTimeTimesheetEntry } from '@/types/access-time.types';
import { format } from 'date-fns';
import { ApprovalHistoryTimeline } from './approval-history-timeline';
import { fetchFinOpsTimesheets } from '@/lib/finops-api';
import { fetchAccessTimeTimesheets } from '@/lib/access-time-api';
import { getDataMode } from '@/lib/data-service';

// DataTable + pagination
import { DataTableEnhanced, type ColumnDef, type ColumnSort, columnDefinitionsToVisibility } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray } from '@/types/pagination';
import type { PaginationMetadata } from '@/types/pagination';

// Column configurator + preferences
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';

// Advanced filter sheet
import {
  TimeEntriesFilterSheet,
  type TimeFilters,
  countActiveTimeFilters,
  applyTimeFilters,
} from './time-entries-filter-sheet';

// Group By utils
import {
  groupTimeEntries,
  TIME_GROUP_BY_OPTIONS,
  type TimeGroupById,
  type TimeEntryGroup,
} from '@/lib/time-group-by-utils';

// ─── Module-level lookup constants (reused in column renderers & detail panel) ─
const ROLE_NAMES: Record<string, string> = {
  'role-1': 'Project Manager',
  'role-2': 'Senior Consultant',
  'role-3': 'Consultant',
  'role-4': 'Junior Consultant',
  'role-5': 'Technical Architect',
  'role-6': 'Business Analyst',
  'role-7': 'Developer',
  'role-8': 'QA Tester',
};

const WORK_TYPE_NAMES: Record<string, string> = {
  'Standard Time':  'Standard Time',
  'Overtime':       'Overtime',
  'Night Shift':    'Night Shift',
  'Weekend':        'Weekend',
  'Bank Holiday':   'Bank Holiday',
  'On Call':        'On Call',
  'Standby':        'Standby',
};

const COST_CENTRE_NAMES: Record<string, string> = {
  'CC-001': 'CC-001 Operations',
  'CC-002': 'CC-002 Technology',
  'CC-003': 'CC-003 Consulting',
  'CC-004': 'CC-004 Support',
};

interface TimeEntriesTabProps {
  projectId: number;
  projectCode?: string;
  timeEntries: TimeEntry[];
  employees: Array<{ id: number; name: string }>;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Pre-select specific employees by name (e.g. from the unbooked timesheeters banner) */
  initialEmployeeNames?: string[];
  /** Pre-filter to billable entries only (e.g. from the Realisation Rate card) */
  initialBillableOnly?: boolean;
}

function mapFinOpsStatusToApproved(status: number): boolean {
  return status === 3;
}

function mapFinOpsTimesheetToTimeEntry(
  entry: FinOpsTimesheetEntry & { statusText?: string; normalizedStatus?: string },
  projectId: number
): TimeEntry {
  // Use enriched status fields from backend if available, otherwise fall back to numeric mapping
  const statusText = entry.statusText;
  const normalizedStatus = entry.normalizedStatus;

  const isApproved = statusText
    ? statusText === 'Approved'
    : mapFinOpsStatusToApproved(entry.status);

  const resolvedStatus = statusText || (() => {
    if (entry.inApprovalFlag) return 'InApproval';
    switch (entry.status) {
      case 0: return 'Draft';
      case 1: return 'Submitted';
      case 2: return 'InApproval';
      case 3: return 'Approved';
      case 4: return 'Rejected';
      default: return 'Unknown';
    }
  })();

  const resolvedNormalized = normalizedStatus || (() => {
    switch (resolvedStatus) {
      case 'Draft': return 'draft';
      case 'Submitted': return 'submitted';
      case 'InApproval': return 'pending';
      case 'Approved': return 'approved';
      case 'Rejected': return 'rejected';
      default: return 'unknown';
    }
  })();

  return {
    id: parseInt(String(entry.primaryKey), 10) || 0,
    projectId,
    employeeId: parseInt(String(entry.platformUserId), 10) || 0,
    employeeName: entry.resourceName || undefined,
    date: entry.timesheetDate.split('T')[0],
    hours: entry.hours,
    description: entry.description || '',
    billable: !entry.isAbsence && !entry.isBooking,
    approved: isApproved,
    status: resolvedStatus,
    normalizedStatus: resolvedNormalized as TimeEntry['normalizedStatus'],
    statusMapping: {
      sourceStatus: String(entry.status),
      mappedTo: resolvedNormalized as 'draft' | 'submitted' | 'pending' | 'approved' | 'rejected',
      mappedAt: new Date().toISOString(),
    },
    costRate: 0,
    billRate: 0,
    costCentreId: entry.costCentreCode,
    roleId: entry.roleCode,
    taskId: entry.taskPrimary > 0 ? entry.taskPrimary : undefined,
  };
}

function mapAccessTimeStatusToApproved(status: string): boolean {
  return status.toLowerCase() === 'approved';
}

function mapAccessTimeTimesheetToTimeEntry(
  entry: AccessTimeTimesheetEntry,
  projectId: number
): TimeEntry {
  const isApproved = mapAccessTimeStatusToApproved(entry.status);
  return {
    id: parseInt(String(entry.id), 10) || 0,
    projectId,
    employeeId: entry.employeeId,
    employeeName: entry.employeeName || undefined,
    date: entry.weekEnding.split('T')[0],
    hours: entry.totalHours,
    description: entry.description || '',
    billable: entry.billable,
    approved: isApproved,
    costRate: 0,
    billRate: 0,
    costCentreId: entry.costCentreCode,
  };
}

interface TimeEntrySummary {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  totalCost: number;
  totalBillableValue: number;
  approvedHours: number;
  pendingHours: number;
  uniqueEmployees: number;
}

// ─── Column definitions ────────────────────────────────────────────────────────
const DEFAULT_TIME_COLUMNS: ColumnDefinition[] = [
  { id: 'date',        name: 'Date',        description: 'Entry date',             category: 'Timeline',  visible: true, locked: true, order: 0 },
  { id: 'employee',    name: 'Employee',    description: 'Employee name',          category: 'Resource',  visible: true, locked: true, order: 1 },
  { id: 'role',        name: 'Role',        description: 'Employee role',          category: 'Resource',       visible: true,  order: 2 },
  { id: 'costCentre',  name: 'Cost Centre', description: 'Cost centre code',       category: 'Financial',      visible: true,  order: 3 },
  { id: 'description', name: 'Description', description: 'Work description',       category: 'Status',    visible: true, order: 4 },
  { id: 'hours',       name: 'Hours',       description: 'Hours logged',           category: 'Financial', visible: true, locked: true, order: 5 },
  { id: 'billable',    name: 'Billable',    description: 'Whether entry is billable', category: 'Financial', visible: true, order: 6 },
  { id: 'status',      name: 'Status',      description: 'Approval status',        category: 'Status',    visible: true, locked: true, order: 7 },
  { id: 'workType',    name: 'Work Type',   description: 'Type of work performed', category: 'Status',         visible: true,  order: 8 },
  { id: 'cost',        name: 'Cost',        description: 'Total cost',             category: 'Financial',      visible: true,  order: 9 },
  { id: 'billValue',   name: 'Bill Value',  description: 'Billable value',         category: 'Financial',      visible: true,  order: 10 },
  { id: 'weekCommencing', name: 'Week Commencing', description: 'Week the entry falls in', category: 'Timeline', visible: false, order: 11 },
];

/** Widget template IDs used by the workspace to route to timesheet products. */
const TIMESHEET_WIDGET_TEMPLATES: Partial<Record<TimesheetProduct, string>> = {
  'access-time': 'access.erp.timetrackpro',
  'access-financial-operations': 'Access.Product.FinancialsOperations',
  'access-focalpoint': 'access.erp.timetrackpro',
};

/**
 * Builds a workspace launch URL for Access Time / FocalPoint products.
 * Returns undefined when required data is missing.
 */
function buildWorkspaceLaunchUrl(
  workspaceUrl: string | undefined,
  timesheetProduct: string | undefined,
  timesheetInstanceId: string | undefined,
): string | undefined {
  if (!workspaceUrl || !timesheetProduct || !timesheetInstanceId) return undefined;

  const widgetTemplate = TIMESHEET_WIDGET_TEMPLATES[timesheetProduct as TimesheetProduct];
  if (!widgetTemplate) return undefined;

  try {
    const url = new URL(workspaceUrl);
    const pathSegments = url.pathname.split('/').filter(Boolean);
    const workspacesIdx = pathSegments.indexOf('workspaces');
    if (workspacesIdx < 0) return undefined;

    const orgBasePath = '/' + pathSegments.slice(0, workspacesIdx).join('/');
    const instanceIdClean = timesheetInstanceId.replace(/-/g, '');

    return `${url.origin}${orgBasePath}/workspaces/launch/${widgetTemplate}/${instanceIdClean}`;
  } catch {
    return undefined;
  }
}

/**
 * Builds a direct FinOps timesheets deep-link URL.
 * Pattern: {finOpsWebUrl}Timesheets/Timesheets.aspx?Open={entryPrimaryKey}&sid={instanceId}
 */
function buildFinOpsTimesheetUrl(
  finOpsWebUrl: string | undefined,
  timesheetInstanceId: string | undefined,
  entryId: number | undefined,
): string | undefined {
  if (!finOpsWebUrl || !timesheetInstanceId || !entryId) return undefined;

  const base = finOpsWebUrl.endsWith('/') ? finOpsWebUrl : finOpsWebUrl + '/';
  const instanceIdClean = timesheetInstanceId.replace(/-/g, '');
  return `${base}Timesheets/Timesheets.aspx?Open=${entryId}&sid=${instanceIdClean}`;
}

/** Builds the appropriate timesheet system URL based on the configured product. */
function buildTimesheetSystemUrl(
  workspaceUrl: string | undefined,
  timesheetProduct: string | undefined,
  timesheetInstanceId: string | undefined,
  finOpsWebUrl: string | undefined,
  entryId: number | undefined,
): string | undefined {
  if (timesheetProduct === 'access-financial-operations') {
    return buildFinOpsTimesheetUrl(finOpsWebUrl, timesheetInstanceId, entryId);
  }
  return buildWorkspaceLaunchUrl(workspaceUrl, timesheetProduct, timesheetInstanceId);
}

export function TimeEntriesTab({
  projectId,
  projectCode,
  timeEntries: propTimeEntries,
  employees,
  loading: propLoading = false,
  error: propError = null,
  onRetry,
  initialEmployeeNames = [],
  initialBillableOnly = false,
}: TimeEntriesTabProps) {
  const { currentCompany } = useCompany();
  const { settings } = useWorkspaceSettings();

  // ── FinOps state ──────────────────────────────────────────────────────
  const [liveTimeEntries, setLiveTimeEntries] = useState<TimeEntry[]>([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [isFinOpsMode, setIsFinOpsMode] = useState(false);
  const [finOpsNotConfigured, setFinOpsNotConfigured] = useState(false);
  const [finOpsWebUrl, setFinOpsWebUrl] = useState<string | null>(null);

  // ── UI state ──────────────────────────────────────────────────────────
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [approvalHistorySheetOpen, setApprovalHistorySheetOpen] = useState(false);
  const [approvalHistoryEntry, setApprovalHistoryEntry] = useState<TimeEntry | null>(null);
  const [approvalHistoryExpanded, setApprovalHistoryExpanded] = useState(false);

  // ── Search / filter state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [quickStatusFilter, setQuickStatusFilter] = useState<string[]>([]);
  const [quickMemberFilter, setQuickMemberFilter] = useState<string[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState<TimeFilters>({});
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // ── Table state ───────────────────────────────────────────────────────
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: 'date', direction: 'desc' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // ── Group By state ────────────────────────────────────────────────────
  const [activeGroupBy, setActiveGroupBy] = useState<TimeGroupById | ''>('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // ── Column preferences ────────────────────────────────────────────────
  const { columns: timeColumns, updateColumns } = useColumnPreferences({
    storageKey: 'time-entries-columns',
    defaultColumns: DEFAULT_TIME_COLUMNS,
  });

  const visibleColumns = useMemo(
    () => timeColumns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [timeColumns]
  );

  // Pre-populate employee filter from names passed in
  useEffect(() => {
    if (initialEmployeeNames.length > 0 && employees.length > 0) {
      const matchedIds = employees
        .filter((e) => initialEmployeeNames.includes(e.name))
        .map((e) => String(e.id));
      if (matchedIds.length > 0) {
        setAdvancedFilters((prev) => ({ ...prev, employees: matchedIds }));
      }
    }
  }, [initialEmployeeNames.join(','), employees.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-filter to billable only if requested (e.g. from Realisation Rate card)
  useEffect(() => {
    if (initialBillableOnly) {
      setAdvancedFilters((prev) => ({ ...prev, billable: ['billable'] }));
    }
  }, [initialBillableOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch FinOps web URL when the company uses FinOps timesheets ────
  useEffect(() => {
    if (currentCompany?.timesheetProduct === 'access-financial-operations') {
      fetchFinOpsWebUrl().then(setFinOpsWebUrl);
    }
  }, [currentCompany?.timesheetProduct]);

  // ── Live timesheet data loading (Access Time or FinOps) ─────────────
  useEffect(() => {
    const loadData = async () => {
      const dataMode = getDataMode();
      const timesheetInstanceId = currentCompany?.timesheetInstanceId;
      const timesheetProduct = currentCompany?.timesheetProduct;

      if (dataMode === 'live' && projectCode) {
        if (timesheetInstanceId) {
          setIsFinOpsMode(true);
          setLiveLoading(true);
          setLiveError(null);
          setFinOpsNotConfigured(false);
          try {
            let mapped: TimeEntry[] = [];
            if (timesheetProduct === 'access-time') {
              const atData = await fetchAccessTimeTimesheets(projectCode);
              if (atData) {
                mapped = atData.map((entry) => mapAccessTimeTimesheetToTimeEntry(entry, projectId));
              }
            } else {
              const finOpsData = await fetchFinOpsTimesheets(projectCode, timesheetInstanceId);
              if (finOpsData) {
                mapped = finOpsData.map((entry) => mapFinOpsTimesheetToTimeEntry(entry, projectId));
              }
            }
            setLiveTimeEntries(mapped);
          } catch (err) {
            console.warn('[TimeEntriesTab] Timesheet API unavailable, falling back to local data:', err);
            setIsFinOpsMode(false);
            setLiveTimeEntries([]);
          } finally {
            setLiveLoading(false);
          }
        } else {
          setFinOpsNotConfigured(true);
          setLiveTimeEntries([]);
          setIsFinOpsMode(false);
        }
      } else {
        setIsFinOpsMode(false);
        setFinOpsNotConfigured(false);
      }
    };
    loadData();
  }, [projectId, projectCode, currentCompany?.timesheetInstanceId, currentCompany?.timesheetProduct]);

  const timeEntries = isFinOpsMode ? liveTimeEntries : propTimeEntries;
  const loading = isFinOpsMode ? liveLoading : propLoading;
  const error = isFinOpsMode ? liveError : propError;

  const getEmployeeName = (employeeId: number, employeeName?: string): string => {
    if (employeeName) return employeeName;
    const employee = employees.find((e) => e.id === employeeId);
    return employee?.name || 'Unknown Employee';
  };

  // ── Derived option lists ──────────────────────────────────────────────
  const uniqueEmployees = useMemo(() => {
    if (isFinOpsMode) {
      const seen = new Map<string, number>();
      for (const e of timeEntries) {
        const name = e.employeeName || 'Unknown Employee';
        if (!seen.has(name)) seen.set(name, e.employeeId);
      }
      return Array.from(seen.entries())
        .map(([name, id]) => ({ value: String(id), label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    const ids = Array.from(new Set(timeEntries.map((e) => e.employeeId)));
    return ids
      .map((id) => ({ value: String(id), label: getEmployeeName(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [timeEntries, employees, isFinOpsMode]);

  // ── Advanced filter option lists ──────────────────────────────────────
  const uniqueRoles = useMemo(() => {
    const values = [...new Set(timeEntries.map((e) => e.roleId).filter(Boolean))] as string[];
    return values
      .sort((a, b) => (ROLE_NAMES[a] ?? a).localeCompare(ROLE_NAMES[b] ?? b))
      .map((v) => ({ value: v, label: ROLE_NAMES[v] ?? v }));
  }, [timeEntries]);

  const uniqueWorkTypes = useMemo(() => {
    const values = [...new Set(timeEntries.map((e) => e.workTypeId).filter(Boolean))] as string[];
    return values.sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
  }, [timeEntries]);

  const uniqueCostCentres = useMemo(() => {
    const values = [...new Set(timeEntries.map((e) => e.costCentreId).filter(Boolean))] as string[];
    return values.sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
  }, [timeEntries]);

  // ── Quick filter option lists ─────────────────────────────────────────
  const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'pending', label: 'In Approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const MEMBER_OPTIONS = useMemo(
    () => uniqueEmployees.map((e) => ({ value: e.value, label: e.label })),
    [uniqueEmployees]
  );

  // ── Summary ───────────────────────────────────────────────────────────
  const summary: TimeEntrySummary = useMemo(() => {
    const totalHours = timeEntries.reduce((s, e) => s + e.hours, 0);
    const billableHours = timeEntries.filter((e) => e.billable).reduce((s, e) => s + e.hours, 0);
    const approvedHours = timeEntries.filter((e) => (e.normalizedStatus === 'approved') || (!e.normalizedStatus && e.approved)).reduce((s, e) => s + e.hours, 0);
    return {
      totalHours,
      billableHours,
      nonBillableHours: totalHours - billableHours,
      totalCost: timeEntries.reduce((s, e) => s + e.hours * e.costRate, 0),
      totalBillableValue: timeEntries.filter((e) => e.billable).reduce((s, e) => s + e.hours * e.billRate, 0),
      approvedHours,
      pendingHours: totalHours - approvedHours,
      uniqueEmployees: new Set(timeEntries.map((e) => e.employeeId)).size,
    };
  }, [timeEntries]);

  // ── Filter + sort + paginate ──────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = [...timeEntries];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.description.toLowerCase().includes(q) ||
          getEmployeeName(e.employeeId, e.employeeName).toLowerCase().includes(q) ||
          e.date.includes(q)
      );
    }

    if (quickStatusFilter.length > 0) {
      result = result.filter((e) => {
        const status = e.normalizedStatus || (e.approved ? 'approved' : 'pending');
        return quickStatusFilter.includes(status);
      });
    }

    if (quickMemberFilter.length > 0) {
      result = result.filter((e) => quickMemberFilter.includes(String(e.employeeId)));
    }

    result = applyTimeFilters(result, advancedFilters);
    return result;
  }, [timeEntries, searchQuery, quickStatusFilter, quickMemberFilter, advancedFilters]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    for (const sort of [...columnSorts].reverse()) {
      result.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        switch (sort.columnId) {
          case 'date':      aVal = new Date(a.date).getTime(); bVal = new Date(b.date).getTime(); break;
          case 'employee':  aVal = getEmployeeName(a.employeeId, a.employeeName); bVal = getEmployeeName(b.employeeId, b.employeeName); break;
          case 'hours':     aVal = a.hours; bVal = b.hours; break;
          case 'cost':      aVal = a.hours * a.costRate; bVal = b.hours * b.costRate; break;
          case 'billValue': aVal = a.billable ? a.hours * a.billRate : 0; bVal = b.billable ? b.hours * b.billRate : 0; break;
          default:          aVal = String((a as unknown as Record<string, unknown>)[sort.columnId] ?? ''); bVal = String((b as unknown as Record<string, unknown>)[sort.columnId] ?? '');
        }
        const cmp = typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
        return sort.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [filtered, columnSorts]);

  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(sorted.length, currentPage, itemsPerPage),
    [sorted.length, currentPage, itemsPerPage]
  );

  const paginatedEntries = useMemo(
    () => paginateArray(sorted, currentPage, itemsPerPage).data,
    [sorted, currentPage, itemsPerPage]
  );

  // Reset page on filter/sort change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, quickStatusFilter, quickMemberFilter, advancedFilters, columnSorts, itemsPerPage]);

  // ── Grouped view (when Group By is active) ────────────────────────────
  const timeGroups = useMemo((): TimeEntryGroup[] => {
    if (!activeGroupBy) return [];
    return groupTimeEntries(sorted, activeGroupBy);
  }, [sorted, activeGroupBy]);

  const isGrouped = !!activeGroupBy;

  const toggleGroupCollapse = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // When grouping changes, reset collapsed state
  useEffect(() => { setCollapsedGroups(new Set()); }, [activeGroupBy]);


  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

  // ── Column defs for DataTableEnhanced ────────────────────────────────
  const columnDefs = useMemo((): ColumnDef<TimeEntry, unknown>[] => {
    const cols: ColumnDef<TimeEntry, unknown>[] = [];
    const vis = columnDefinitionsToVisibility(visibleColumns);

    if (vis['date']) cols.push({
      id: 'date',
      header: 'Date',
      accessorFn: (r) => r.date,
      cell: ({ row }) => (
        <button
          onClick={() => setSelectedEntry(row.original)}
          className="text-primary hover:underline focus:outline-none rounded font-medium"
        >
          {format(new Date(row.original.date), 'dd MMM yyyy')}
        </button>
      ),
    });

    if (vis['employee']) cols.push({
      id: 'employee',
      header: 'Employee',
      accessorFn: (r) => getEmployeeName(r.employeeId, r.employeeName),
    });

    if (vis['description']) cols.push({
      id: 'description',
      header: 'Description',
      accessorFn: (r) => r.description,
      cell: ({ row }) => (
        <span className="max-w-[300px] truncate block">{row.original.description}</span>
      ),
    });

    if (vis['hours']) cols.push({
      id: 'hours',
      header: 'Hours',
      accessorFn: (r) => r.hours,
      cell: ({ row }) => <span className="font-medium">{row.original.hours.toFixed(2)}</span>,
      meta: { align: 'right' },
    });

    if (vis['billable']) cols.push({
      id: 'billable',
      header: 'Billable',
      enableSorting: false,
      accessorFn: (r) => r.billable ? 'Billable' : 'Non-Billable',
      cell: ({ row }) => row.original.billable ? (
        <Badge variant="default" accent="info" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />Billable
        </Badge>
      ) : (
        <Badge variant="secondary" className="gap-1">
          <XCircle className="h-3 w-3" />Non-Billable
        </Badge>
      ),
    });

    if (vis['status']) cols.push({
      id: 'status',
      header: 'Status',
      enableSorting: false,
      accessorFn: (r) => r.normalizedStatus || (r.approved ? 'approved' : 'pending'),
      cell: ({ row }) => {
        const entry = row.original;
        const normalized = entry.normalizedStatus || (entry.approved ? 'approved' : 'pending');
        const handleClick = (e: React.MouseEvent) => { e.stopPropagation(); setApprovalHistoryEntry(entry); setApprovalHistorySheetOpen(true); };

        switch (normalized) {
          case 'approved':
            return (
              <Badge variant="success" accent="success" className="gap-1 cursor-pointer hover:opacity-80" onClick={handleClick}>
                <CheckCircle2 className="h-3 w-3" />Approved
              </Badge>
            );
          case 'rejected':
            return (
              <Badge variant="destructive" className="gap-1 cursor-pointer hover:opacity-80" onClick={handleClick}>
                <XCircle className="h-3 w-3" />Rejected
              </Badge>
            );
          case 'pending':
            return (
              <Badge variant="warning" accent="warning" className="gap-1 cursor-pointer hover:opacity-80" onClick={handleClick}>
                <Clock className="h-3 w-3" />In Approval
              </Badge>
            );
          case 'submitted':
            return (
              <Badge variant="outline" className="gap-1 border-blue-300 text-blue-700 cursor-pointer hover:opacity-80" onClick={handleClick}>
                <Clock className="h-3 w-3" />Submitted
              </Badge>
            );
          case 'draft':
            return (
              <Badge variant="secondary" className="gap-1 cursor-pointer hover:opacity-80" onClick={handleClick}>
                <Clock className="h-3 w-3" />Draft
              </Badge>
            );
          default:
            return (
              <Badge variant="warning" accent="warning" className="gap-1 cursor-pointer hover:opacity-80" onClick={handleClick}>
                <Clock className="h-3 w-3" />Pending
              </Badge>
            );
        }
      },
    });

    if (vis['workType']) cols.push({
      id: 'workType',
      header: 'Work Type',
      accessorFn: (r) => r.workTypeId ?? '—',
      cell: ({ row }) => row.original.workTypeId ? (
        <span className="inline-flex items-center gap-1.5">
          <Tag className="h-3 w-3 text-muted-foreground" />
          {row.original.workTypeId}
        </span>
      ) : <span className="text-muted-foreground">—</span>,
    });

    if (vis['role']) cols.push({
      id: 'role',
      header: 'Role',
      accessorFn: (r) => r.roleId ?? '—',
      cell: ({ row }) => {
        const roleId = row.original.roleId;
        return roleId ? (
          <span className="inline-flex items-center gap-1.5">
            <Briefcase className="h-3 w-3 text-muted-foreground" />
            {ROLE_NAMES[roleId] ?? roleId}
          </span>
        ) : <span className="text-muted-foreground">—</span>;
      },
    });

    if (vis['cost']) cols.push({
      id: 'cost',
      header: 'Cost',
      accessorFn: (r) => r.hours * r.costRate,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatCurrency(row.original.hours * row.original.costRate)}
        </span>
      ),
      meta: { align: 'right' },
    });

    if (vis['billValue']) cols.push({
      id: 'billValue',
      header: 'Bill Value',
      accessorFn: (r) => r.billable ? r.hours * r.billRate : 0,
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.billable ? formatCurrency(row.original.hours * row.original.billRate) : '—'}
        </span>
      ),
      meta: { align: 'right' },
    });

    if (vis['costCentre']) cols.push({
      id: 'costCentre',
      header: 'Cost Centre',
      accessorFn: (r) => r.costCentreId ?? '—',
      cell: ({ row }) => row.original.costCentreId ? (
        <span className="inline-flex items-center gap-1.5">
          <Building2 className="h-3 w-3 text-muted-foreground" />
          {row.original.costCentreId}
        </span>
      ) : <span className="text-muted-foreground">—</span>,
    });

    if (vis['weekCommencing']) cols.push({
      id: 'weekCommencing',
      header: 'Week Commencing',
      accessorFn: (r) => {
        const d = new Date(r.date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff)).toISOString();
      },
      cell: ({ row }) => {
        const d = new Date(row.original.date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(new Date(row.original.date).setDate(diff));
        return (
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3 text-muted-foreground" />
            {format(monday, 'dd MMM yyyy')}
          </span>
        );
      },
    });

    return cols;
  }, [visibleColumns, timeEntries, employees, isFinOpsMode]);

  // ── Export handler ────────────────────────────────────────────────────
  const handleExportCsv = () => {
    const headers = ['Date', 'Week Commencing', 'Employee', 'Role', 'Work Type', 'Cost Centre', 'Description', 'Hours', 'Billable', 'Status', 'Cost', 'Bill Value'];
    const rows = sorted.map((e) => {
      const d = new Date(e.date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(new Date(e.date).setDate(diff));
      return [
        format(new Date(e.date), 'dd/MM/yyyy'),
        format(monday, 'dd/MM/yyyy'),
        getEmployeeName(e.employeeId, e.employeeName),
        e.roleId ?? '',
        e.workTypeId ?? '',
        e.costCentreId ?? '',
        e.description,
        e.hours.toFixed(1),
        e.billable ? 'Yes' : 'No',
        e.status || (e.approved ? 'Approved' : 'Pending'),
        formatCurrency(e.hours * e.costRate),
        e.billable ? formatCurrency(e.hours * e.billRate) : '—',
      ];
    });
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `time-entries-${projectCode ?? projectId}-${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Retry handler ─────────────────────────────────────────────────────
  const handleRetry = async () => {
    if (isFinOpsMode || finOpsNotConfigured) {
      const dataMode = getDataMode();
      const timesheetInstanceId = currentCompany?.timesheetInstanceId;
      const timesheetProduct = currentCompany?.timesheetProduct;
      if (dataMode === 'live' && projectCode) {
        if (timesheetInstanceId) {
          setIsFinOpsMode(true);
          setLiveLoading(true);
          setLiveError(null);
          setFinOpsNotConfigured(false);
          try {
            let mapped: TimeEntry[] = [];
            if (timesheetProduct === 'access-time') {
              const atData = await fetchAccessTimeTimesheets(projectCode);
              if (atData) {
                mapped = atData.map((e) => mapAccessTimeTimesheetToTimeEntry(e, projectId));
              }
            } else {
              const finOpsData = await fetchFinOpsTimesheets(projectCode, timesheetInstanceId);
              if (finOpsData) {
                mapped = finOpsData.map((e) => mapFinOpsTimesheetToTimeEntry(e, projectId));
              }
            }
            setLiveTimeEntries(mapped);
          } catch (err) {
            console.warn('[TimeEntriesTab] Timesheet API unavailable, falling back to local data:', err);
            setIsFinOpsMode(false);
            setLiveTimeEntries([]);
          } finally {
            setLiveLoading(false);
          }
        } else {
          setFinOpsNotConfigured(true);
          setLiveTimeEntries([]);
        }
      }
    } else if (onRetry) {
      onRetry();
    }
  };

  // ── Early returns ─────────────────────────────────────────────────────
  if (finOpsNotConfigured) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Link2Off className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Timesheet Integration Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  A timesheet integration has not been set up for this company. Please configure the integration in Settings to view timesheet data.
                </p>
              </div>
              <Button variant="outline" onClick={handleRetry}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center divide-x">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex-1 px-6 first:pl-4 last:pr-4">
                  <Skeleton className="h-3 w-24 mb-2" />
                  <Skeleton className="h-6 w-32" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Error Loading Data</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              {onRetry && <Button onClick={handleRetry}>Retry</Button>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (timeEntries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Clock className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Time Entries</h3>
                <p className="text-sm text-muted-foreground">
                  There are no time entries associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeFilterCount = countActiveTimeFilters(advancedFilters);

  return (
    <div className="space-y-6">
      {/* Time Summary KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <CompactKpiCard
          title="Total Hours"
          value={summary.totalHours.toFixed(1)}
          icon={Clock}
          accentVariant="info"
          description={`${summary.billableHours.toFixed(1)} billable, ${summary.nonBillableHours.toFixed(1)} non-billable`}
        />
        <CompactKpiCard
          title="Billable Value"
          value={formatCurrency(summary.totalBillableValue)}
          icon={TrendingUp}
          accentVariant="success"
          description={`From ${summary.billableHours.toFixed(1)} billable hours`}
        />
        {(() => {
          const approvedPct = summary.totalHours > 0 ? (summary.approvedHours / summary.totalHours) * 100 : 0;
          const accentVariant = approvedPct >= 80 ? 'success' : approvedPct >= 50 ? 'warning' : 'error';
          return (
            <CompactKpiCard
              title="Approved Hours"
              value={summary.approvedHours.toFixed(1)}
              icon={CheckCircle2}
              accentVariant={accentVariant}
              description={`${approvedPct.toFixed(0)}% of total hours`}
            />
          );
        })()}
        <CompactKpiCard
          title="Pending Hours"
          value={summary.pendingHours.toFixed(1)}
          icon={TimerOff}
          accentVariant={summary.pendingHours > 0 ? 'warning' : 'success'}
          description={`${summary.totalHours > 0 ? ((summary.pendingHours / summary.totalHours) * 100).toFixed(0) : 0}% not yet approved`}
        />
      </div>

      {/* Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Time Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Toolbar + results count */}
          <div className="px-4 pt-4 pb-3">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
              {/* Search */}
              <div className="relative w-52 shrink-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search entries..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-8 h-8 text-sm"
                />
              </div>

              {/* Quick filter — Status */}
              <div className="w-40 shrink-0">
                <MultiSelect
                  options={STATUS_OPTIONS}
                  placeholder="Status"
                  value={quickStatusFilter}
                  onValueChange={setQuickStatusFilter}
                  maxDisplay={1}
                  size="sm"
                />
              </div>

              {/* Quick filter — Team Member */}
              <div className="w-48 shrink-0">
                <MultiSelect
                  options={MEMBER_OPTIONS}
                  placeholder="Team Member"
                  value={quickMemberFilter}
                  onValueChange={setQuickMemberFilter}
                  maxDisplay={1}
                  size="sm"
                />
              </div>

              {/* Clear quick filters */}
              {(quickStatusFilter.length > 0 || quickMemberFilter.length > 0) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs text-muted-foreground shrink-0"
                  onClick={() => { setQuickStatusFilter([]); setQuickMemberFilter([]); }}
                >
                  Clear
                </Button>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Group By */}
              <div className="w-44 shrink-0">
                <MultiSelect
                  options={TIME_GROUP_BY_OPTIONS.map((o) => ({ label: o.label, value: o.id }))}
                  placeholder="Group by..."
                  value={activeGroupBy ? [activeGroupBy] : []}
                  onValueChange={(vals) => setActiveGroupBy((vals[vals.length - 1] ?? '') as TimeGroupById | '')}
                  maxDisplay={1}
                  size="sm"
                />
              </div>

              {/* Advanced Filter */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 relative shrink-0"
                aria-label="Advanced Filters"
                onClick={() => setFilterSheetOpen(true)}
              >
                <Filter className="h-3.5 w-3.5" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {/* Column configurator */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                aria-label="Configure columns"
                onClick={() => setColumnConfigOpen(true)}
              >
                <Settings2 className="h-3.5 w-3.5" />
              </Button>

              {/* Export to CSV */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                aria-label="Export to CSV"
                onClick={handleExportCsv}
                disabled={sorted.length === 0}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
          </div>

          {/* Results count */}
          <p className="text-sm text-muted-foreground mb-3">
            {isGrouped
              ? `${timeGroups.length} group${timeGroups.length !== 1 ? 's' : ''} · ${sorted.length} entries`
              : `Showing ${paginatedEntries.length} of ${sorted.length} entries`}
          </p>
          </div>

          {/* ── Grouped view ────────────────────────────────────────── */}
          {isGrouped ? (
            <div className="space-y-3">
              {timeGroups.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                  <TrendingUp className="h-8 w-8" />
                  <p>No entries match your filters</p>
                </div>
              ) : (
                timeGroups.map((group) => {
                  const collapsed = collapsedGroups.has(group.key);
                  return (
                    <div key={group.key} className="border rounded-lg overflow-hidden">
                      {/* Group header */}
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapse(group.key)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
                      >
                        {collapsed
                          ? <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                        <span className="text-sm font-medium flex-1">{group.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {group.subtotals.count} {group.subtotals.count === 1 ? 'entry' : 'entries'}
                          {' · '}
                          {group.subtotals.totalHours.toFixed(1)}h total
                          {group.subtotals.pendingHours > 0 && (
                            <span className="text-warning ml-2">
                              · {group.subtotals.pendingHours.toFixed(1)}h pending
                            </span>
                          )}
                        </span>
                      </button>

                      {/* Group rows */}
                      {!collapsed && (
                        <DataTableEnhanced
                          data={group.entries}
                          columns={columnDefs}
                          columnVisibility={columnDefinitionsToVisibility(visibleColumns)}
                          columnSorts={columnSorts}
                          onColumnSortChange={(newSorts) => { setColumnSorts(newSorts); }}
                          emptyMessage={
                            <div className="flex flex-col items-center gap-2 py-4 text-muted-foreground">
                              <p>No entries in this group</p>
                            </div>
                          }
                        />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* ── Flat (default) view ────────────────────────────────── */
            <DataTableEnhanced
              className="rounded-none"
              data={paginatedEntries}
              columns={columnDefs}
              columnVisibility={columnDefinitionsToVisibility(visibleColumns)}
              columnSorts={columnSorts}
              onColumnSortChange={(newSorts) => { setColumnSorts(newSorts); setCurrentPage(1); }}
              emptyMessage={
                <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                  <TrendingUp className="h-8 w-8" />
                  <p>No entries match your filters</p>
                </div>
              }
              paginationContent={
                <PaginationControls
                  pagination={pagination}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                />
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!selectedEntry} onOpenChange={(open) => { if (!open) setSelectedEntry(null); }}>
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          {selectedEntry && (() => {
            const entryDate = new Date(selectedEntry.date);
            // Week commencing (Monday of the entry's week)
            const dayOfWeek = entryDate.getDay();
            const diff = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
            const weekStart = new Date(entryDate);
            weekStart.setDate(entryDate.getDate() + diff);
            const roleName      = selectedEntry.roleId      ? (ROLE_NAMES[selectedEntry.roleId]           ?? selectedEntry.roleId)      : null;
            const workTypeName  = selectedEntry.workTypeId  ? (WORK_TYPE_NAMES[selectedEntry.workTypeId]  ?? selectedEntry.workTypeId)  : null;
            const costCentreName = selectedEntry.costCentreId ? (COST_CENTRE_NAMES[selectedEntry.costCentreId] ?? selectedEntry.costCentreId) : null;

            return (
              <>
                {/* ── Header ─────────────────────────────────────────── */}
                <SheetHeader className="px-6 pt-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <SheetTitle>Time Entry Details</SheetTitle>
                  </div>
                  <SheetDescription className="space-y-1 pt-1">
                    <div className="text-sm text-muted-foreground">
                      {format(entryDate, 'EEEE, dd MMMM yyyy')}
                      <span className="mx-2 text-border">·</span>
                      W/C {format(weekStart, 'dd MMM yyyy')}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                      <span>Internal ID: <span className="font-mono font-medium text-foreground">{selectedEntry.id}</span></span>
                      {selectedEntry.externalTimesheetId && (
                        <span>Timesheet ID: <span className="font-mono font-medium text-foreground">{selectedEntry.externalTimesheetId}</span></span>
                      )}
                      {selectedEntry.sourceSystem && (
                        <span>Source: <span className="font-medium text-foreground">{selectedEntry.sourceSystem}</span></span>
                      )}
                    </div>
                  </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

                  {/* ── Status badges ──────────────────────────────────── */}
                  <div className="flex flex-wrap gap-2">
                    {selectedEntry.billable ? (
                      <Badge variant="default" accent="info" className="gap-1"><CheckCircle2 className="h-3 w-3" />Billable</Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1"><XCircle className="h-3 w-3" />Non-Billable</Badge>
                    )}
                    {selectedEntry.approved ? (
                      <Badge variant="success" accent="success" className="gap-1"><CheckCircle2 className="h-3 w-3" />Approved</Badge>
                    ) : (
                      <Badge variant="warning" accent="warning" className="gap-1"><Clock className="h-3 w-3" />Pending Approval</Badge>
                    )}
                    {selectedEntry.locked && (
                      <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Period Locked{selectedEntry.lockReason ? ` — ${selectedEntry.lockReason}` : ''}</Badge>
                    )}
                  </div>

                  {/* ── Entry Details ──────────────────────────────────── */}
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Entry Details</h4>
                    <div className="border rounded-lg divide-y">
                      <div className="grid grid-cols-2 divide-x">
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Employee</label>
                          <p className="mt-0.5 text-sm font-medium">{getEmployeeName(selectedEntry.employeeId, selectedEntry.employeeName)}</p>
                        </div>
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Role</label>
                          <p className="mt-0.5 text-sm font-medium">{roleName ?? <span className="text-muted-foreground">—</span>}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 divide-x">
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Work Type</label>
                          <p className="mt-0.5 text-sm font-medium">{workTypeName ?? <span className="text-muted-foreground">—</span>}</p>
                        </div>
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Cost Centre</label>
                          <p className="mt-0.5 text-sm font-medium">{costCentreName ?? <span className="text-muted-foreground">—</span>}</p>
                        </div>
                      </div>
                      <div className="px-4 py-3">
                        <label className="text-xs text-muted-foreground">Description</label>
                        <p className="mt-0.5 text-sm">{selectedEntry.description || <span className="text-muted-foreground">No description</span>}</p>
                      </div>
                      <div className="px-4 py-3">
                        <label className="text-xs text-muted-foreground">Hours</label>
                        <p className="mt-0.5 text-lg font-semibold">{selectedEntry.hours.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  {/* ── Financial Details ──────────────────────────────── */}
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Financial Details</h4>
                    <div className="border rounded-lg divide-y">
                      <div className="grid grid-cols-2 divide-x">
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Cost Rate</label>
                          <p className="mt-0.5 text-sm font-medium">{formatCurrency(selectedEntry.costRate)}/hr</p>
                        </div>
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Total Cost</label>
                          <p className="mt-0.5 text-sm font-semibold">{formatCurrency(selectedEntry.hours * selectedEntry.costRate)}</p>
                        </div>
                      </div>
                      {selectedEntry.billable && (
                        <div className="grid grid-cols-2 divide-x">
                          <div className="px-4 py-3">
                            <label className="text-xs text-muted-foreground">Bill Rate</label>
                            <p className="mt-0.5 text-sm font-medium">{formatCurrency(selectedEntry.billRate)}/hr</p>
                          </div>
                          <div className="px-4 py-3">
                            <label className="text-xs text-muted-foreground">Bill Value</label>
                            <p className="mt-0.5 text-sm font-semibold">{formatCurrency(selectedEntry.hours * selectedEntry.billRate)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Approval History ───────────────────────────────── */}
                  {selectedEntry.approvalHistory && selectedEntry.approvalHistory.length > 0 && (
                    <div className="space-y-1">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Approval History
                        <span className="ml-2 normal-case font-normal text-muted-foreground">({selectedEntry.approvalHistory.length} {selectedEntry.approvalHistory.length === 1 ? 'event' : 'events'})</span>
                      </h4>
                      <ApprovalHistoryTimeline
                        approvalHistory={selectedEntry.approvalHistory}
                        timeEntryId={String(selectedEntry.id)}
                      />
                    </div>
                  )}

                  {/* ── View in system ─────────────────────────────────── */}
                  {(() => {
                    const tsUrl = buildTimesheetSystemUrl(
                      settings.workspaceUrl,
                      currentCompany?.timesheetProduct,
                      currentCompany?.timesheetInstanceId,
                      finOpsWebUrl ?? undefined,
                      selectedEntry.id,
                    );
                    const productLabel = currentCompany?.timesheetProduct
                      ? TIMESHEET_PRODUCT_LABELS[currentCompany.timesheetProduct as TimesheetProduct] ?? 'Timesheet System'
                      : 'Timesheet System';
                    return (
                      <div className="pt-2">
                        <Button className="w-full" variant="outline" asChild>
                          <a href={tsUrl ?? '#'} target={tsUrl ? '_blank' : undefined} rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                            <ExternalLink className="h-4 w-4" />
                            View in {productLabel}
                          </a>
                        </Button>
                      </div>
                    );
                  })()}

                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Approval History Sheet / Dialog */}
      {approvalHistoryExpanded ? (
        <Dialog open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>Approval History for Time Entry</DialogTitle>
                  <DialogDescription>
                    {approvalHistoryEntry && (
                      <>{format(new Date(approvalHistoryEntry.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryEntry.employeeId, approvalHistoryEntry.employeeName)}</>
                    )}
                  </DialogDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setApprovalHistoryExpanded(false)} title="Return to sheet view">
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryEntry?.approvalHistory && (
                <ApprovalHistoryTimeline approvalHistory={approvalHistoryEntry.approvalHistory} timeEntryId={String(approvalHistoryEntry.id)} />
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
            <SheetHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>Approval History for Time Entry</SheetTitle>
                  <SheetDescription>
                    {approvalHistoryEntry && (
                      <>{format(new Date(approvalHistoryEntry.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryEntry.employeeId, approvalHistoryEntry.employeeName)}</>
                    )}
                  </SheetDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setApprovalHistoryExpanded(true)} title="Expand to full view">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryEntry?.approvalHistory && (
                <ApprovalHistoryTimeline approvalHistory={approvalHistoryEntry.approvalHistory} timeEntryId={String(approvalHistoryEntry.id)} />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Column Configurator */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={timeColumns}
        onSave={updateColumns}
        defaultColumns={DEFAULT_TIME_COLUMNS}
        pageKey="time-entries"
      />

      {/* Advanced Filter Sheet */}
      <TimeEntriesFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={advancedFilters}
        onApply={(f) => { setAdvancedFilters(f); setCurrentPage(1); }}
        onClear={() => { setAdvancedFilters({}); setCurrentPage(1); }}
        availableEmployees={uniqueEmployees}
        availableRoles={uniqueRoles}
        availableWorkTypes={uniqueWorkTypes}
        availableCostCentres={uniqueCostCentres}
      />
    </div>
  );
}
